/* eslint-disable @typescript-eslint/no-explicit-any */
import type { SerializableHead } from '@unhead/vue/types'
import type { PipeEventProcessorDef, PipeInterface } from '@render/composables/usePipe'
import type { PipeBuildConfigList } from '@render/types/pipeConfigs'

export interface PipeIntegrationDef {
  // If config is needed during execution,
  // It should be persisted in the pipe's state during setup(pipe, config).
  getHead?: (config: any) => SerializableHead
  // Runs immediately when the extension is registered
  setup?: (pipe: PipeInterface, config: any) => void
  // Runs strictly after pipe.initialize() has finished processing data
  // If config is needed during execution,
  // It should be persisted in the pipe's state during setup(pipe, config).
  onReady?: (pipe: PipeInterface, config: any) => void
  // Called immediately after data is loaded from local storage
  onDataLoaded?: (pipe: PipeInterface) => void
  // Integrations can return an array of custom processors
  // Config is only available during setup. If config is needed during execution,
  // It should be persisted in the pipe's state during setup(pipe, config).
  getEventProcessors?: (config: any) => PipeEventProcessorDef[]
  // When true, pipe.whenIntegrationsReady() waits for this integration's
  // onReady() promise to settle before resolving. Use for integrations that
  // fetch data other components depend on (e.g. PIPEORDERS restoring order vars).
  blocking?: boolean
}

// An integration can either be the definition object itself, or a factory function that returns one.
export type PipeIntegration = PipeIntegrationDef | (() => PipeIntegrationDef)

export interface IntegrationItem {
  type: string
  key: string
  config: object
}

/** Data available to an integration's build-time config resolver. */
export interface IntegrationBuildContext {
  // Flat, distribution-resolved release constants: Record<"group.name", value>
  // (e.g. ctx.constants['global.cookieConsentEnabled']). Always defined — the
  // build plugin passes {} when the release snapshot carries no constants.
  // NOTE: this is the FLAT runtime shape, not the (mistyped-as-nested)
  // PipeDto.constants from types.gen.ts.
  constants: Record<string, unknown>
  // Build-time pipe configs (PIPE_CONFIGS_JSON), if present.
  pipeConfigs?: PipeBuildConfigList
}

export interface IntegrationRegistryItem {
  singleton: boolean
  path: string
  // Standard dynamic import returning the module Promise
  loader: () => Promise<any>
  // An integration is activated when any of:
  //   1. The pipe has the integration explicitly enabled (DB config entry), OR
  //   2. At least one of these component types is used anywhere in the pipe, OR
  //   3. activateIfBuildConfig(ctx) returns true (see below).
  applyIfHasComponents?: string[]
  // Optional build-time activation predicate — activates the integration with
  // no DB integration row when the build already carries everything it needs
  // (e.g. KLAVIYO activates off its pipe config's public key). Runs in
  // builder/lib/pages.ts (Node), so it must be PURE (no Vue/runtime imports).
  // The auto-added item starts with an empty config; resolveBuildConfig() is
  // what fills it in.
  activateIfBuildConfig?: (ctx: IntegrationBuildContext) => boolean
  // The checkout this integration pre-warms. When CHECKOUT_VERSION_CONSTANT names a
  // provider for this distribution, the build activates only the matching
  // integration (builder/lib/pages.ts getActiveIntegrations).
  checkoutProvider?: CheckoutProvider
  // When true, getHead() is skipped during SSR and injected client-side only.
  // Use for third-party analytics/tracking scripts that don't benefit from SSR
  // and cause pre-hydration load errors when blocked by ad-blockers.
  headClientOnly?: boolean
  // Optional build-time config resolver. Runs in the Rollup plugin (Node), NOT
  // at runtime, so getHead()/SSR hooks that have no pipe instance can read
  // build/release-resolved values. Must be a PURE function (no Vue/runtime
  // imports). The returned object is merged onto the integration's runtime
  // config in builder/lib/integrations.ts.
  resolveBuildConfig?: (ctx: IntegrationBuildContext) => Record<string, unknown>
}

// First Klaviyo public key delivered by the orders API for this build, if any.
// Shared by KLAVIYO's activation predicate and its build-config resolver so the
// two can never disagree about whether Klaviyo is configured.
function klaviyoPublicKey(pipeConfigs?: PipeBuildConfigList): string | undefined {
  return (
    pipeConfigs?.configs.find((c) => c.type === 'KLAVIYO')?.publicData?.accounts?.[0]?.publicKey ||
    undefined
  )
}

// One pipe may hold both checkouts, each behind a visibility rule on this constant, with the
// value overridden per distribution. The build then activates only the selected provider's
// integration (builder/lib/pages.ts getActiveIntegrations) and GTM tags with the same value.
// Absent or unrecognised → no gating, so single-provider pipes are unaffected.
export const CHECKOUT_VERSION_CONSTANT = 'global.checkoutVersion'

export type CheckoutProvider = 'primer' | 'yuno'

export const normalizeCheckoutProvider = (value: unknown): CheckoutProvider | undefined => {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return v === 'primer' || v === 'yuno' ? v : undefined
}

export const IntegrationRegistry: Record<string, IntegrationRegistryItem> = {
  ga4: {
    singleton: true,
    path: './ga4/ga4',
    loader: () => import('./ga4/ga4'),
  },
  PIPEORDERS: {
    path: './pipeorders/pipeorders',
    loader: () => import('./pipeorders/pipeorders'),
    singleton: true,
  },
  RAW: {
    path: './raw/raw.integration',
    loader: () => import('./raw/raw.integration'),
    singleton: false,
  },
  GTM: {
    path: './gtm/gtm',
    loader: () => import('./gtm/gtm'),
    singleton: true,
    headClientOnly: true,
    // Resolve the per-distribution cookie-consent feature flag from the
    // (already distribution-resolved) release constants at build time so
    // getHead() can decide whether to emit the Google Consent Mode default
    // without a pipe instance. `=== true`: cookie consent is OFF unless
    // explicitly turned on, so absent → disabled and an un-seeded build is
    // safe (tags fire, no banner).
    resolveBuildConfig: ({ constants }) => ({
      cookieConsentEnabled: constants['global.cookieConsentEnabled'] === true,
    }),
  },
  BUGSNAG: {
    path: './bugsnag/bugsnag',
    loader: () => import('./bugsnag/bugsnag'),
    singleton: true,
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  FITNESS_FUNCTIONS: {
    path: './fitnessfunctions/fitnessfunctions',
    loader: () => import('./fitnessfunctions/fitnessfunctions'),
    singleton: true,
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  TRACKING_PROPERTIES: {
    path: './trackingProperties/trackingProperties',
    loader: () => import('./trackingProperties/trackingProperties'),
    singleton: true,
  },
  KLAVIYO: {
    path: './klaviyo/klaviyo',
    loader: () => import('./klaviyo/klaviyo'),
    singleton: true,
    headClientOnly: true,
    // Klaviyo is configured entirely on the orders API (pipe configs / brand
    // fallback), so it needs no pipehouse integration row: it activates
    // wherever the resolved configs carry a public key. Legacy KLAVIYO rows
    // still activate it the normal way — the DB row wins the dedupe check in
    // getActiveIntegrations(), and resolveBuildConfig then overwrites whatever
    // stale key that row holds with the one from the orders API.
    activateIfBuildConfig: ({ pipeConfigs }) => !!klaviyoPublicKey(pipeConfigs),
    resolveBuildConfig: ({ pipeConfigs }) => {
      const publicKey = klaviyoPublicKey(pipeConfigs)
      return publicKey ? { publicKey } : {}
    },
  },
  SEGMENT: {
    path: './segment/segment',
    loader: () => import('./segment/segment'),
    singleton: true,
    headClientOnly: true,
  },
  PRIMER: {
    path: './primer/primer',
    loader: () => import('./primer/primer'),
    singleton: true,
    applyIfHasComponents: ['PrimerCheckout'],
    checkoutProvider: 'primer',
  },
  YUNO: {
    path: './yuno/yuno',
    loader: () => import('./yuno/yuno'),
    singleton: true,
    applyIfHasComponents: ['YunoCheckout'],
    checkoutProvider: 'yuno',
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  GEO_DEVICE: {
    path: './geoDevice/geoDevice',
    loader: () => import('./geoDevice/geoDevice'),
    singleton: true,
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  EXIT_INTENT: {
    path: './exitIntent/exitIntent',
    loader: () => import('./exitIntent/exitIntent'),
    singleton: true,
  },
}
